import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../utils/api';

const ApiKeysSettings = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/userApiKeys');
      setKeys(res.data.keys || []);
    } catch (err) {
      console.error('Failed to load API keys', err);
      toast.error('Could not load API keys.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error('Give the key a name.');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/userApiKeys', { name: newKeyName.trim() });
      setCreatedKey(res.data.key);
      setNewKeyName('');
      setShowCreate(false);
      await load();
    } catch (err) {
      console.error('Failed to create API key', err);
      toast.error(err.response?.data?.error || 'Could not create API key.');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id, name) {
    if (!window.confirm(`Revoke "${name}"? Anything using this key will stop working immediately.`)) return;
    setRevoking(id);
    try {
      await api.delete(`/userApiKeys?id=${encodeURIComponent(id)}`);
      toast.success('API key revoked.');
      await load();
    } catch (err) {
      console.error('Failed to revoke API key', err);
      toast.error('Could not revoke API key.');
    } finally {
      setRevoking(null);
    }
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Select and copy manually.');
    }
  }

  const card = { borderRadius: 'var(--radius-xl)', background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-sm)' };
  const inputStyle = { border: '1px solid var(--lodge-onyx-100)', background: 'var(--lodge-white)', color: 'var(--lodge-onyx-900)' };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--lodge-onyx-300)' }} /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 22, fontWeight: 700, color: 'var(--lodge-onyx-900)' }}>API Keys</h1>
          <p style={{ fontSize: 13, color: 'var(--lodge-onyx-500)', marginTop: 4 }}>
            Personal access tokens for scripts and external integrations. Treat them like passwords.
          </p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setCreatedKey(null); }}
          className="px-3 py-2 text-sm font-semibold rounded-lg text-white flex items-center gap-2"
          style={{ background: 'var(--lodge-orange-400)' }}>
          <Plus size={14} /> New API Key
        </button>
      </div>

      {/* One-time secret reveal */}
      {createdKey && (
        <div style={{ ...card, borderColor: 'var(--lodge-orange-200)', background: 'var(--lodge-orange-50)' }} className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} style={{ color: 'var(--lodge-orange-500)', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1 min-w-0">
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>
                Copy this key now — it won't be shown again
              </div>
              <p style={{ fontSize: 12, color: 'var(--lodge-onyx-600)', marginTop: 4 }}>
                "{createdKey.name}" — store this somewhere safe. If you lose it, you'll need to create a new one.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <code className="flex-1 px-3 py-2 rounded-lg font-mono text-xs break-all"
                  style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', color: 'var(--lodge-onyx-900)' }}>
                  {createdKey.raw}
                </code>
                <button onClick={() => copyToClipboard(createdKey.raw)}
                  className="p-2 rounded-lg shrink-0"
                  style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)' }}
                  title="Copy">
                  {copied ? <Check size={14} style={{ color: 'var(--color-success)' }} /> : <Copy size={14} style={{ color: 'var(--lodge-onyx-500)' }} />}
                </button>
              </div>
              <button onClick={() => setCreatedKey(null)}
                className="mt-3 text-xs font-medium" style={{ color: 'var(--lodge-onyx-500)' }}>
                I've saved it — dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} style={card} className="p-4 space-y-3">
          <div className="space-y-1">
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--lodge-onyx-700)' }}>Name</label>
            <input
              type="text"
              autoFocus
              placeholder="e.g. ETL job, Zapier integration"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              maxLength={80}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={inputStyle}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowCreate(false); setNewKeyName(''); }}
              className="px-3 py-1.5 text-sm rounded-lg"
              style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-700)' }}>
              Cancel
            </button>
            <button type="submit" disabled={creating || !newKeyName.trim()}
              className="px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
              style={{ background: 'var(--lodge-orange-400)' }}>
              {creating ? 'Creating...' : 'Create Key'}
            </button>
          </div>
        </form>
      )}

      {/* Keys list */}
      <div style={card}>
        {keys.length === 0 ? (
          <div className="p-8 text-center">
            <Key size={28} style={{ color: 'var(--lodge-onyx-300)', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14, color: 'var(--lodge-onyx-500)' }}>No API keys yet.</p>
            <p style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 4 }}>
              Create one to access the API from scripts or external services.
            </p>
          </div>
        ) : (
          <div>
            {keys.map((k, i) => (
              <div key={k.id}
                className="flex items-center justify-between p-4"
                style={{ borderBottom: i < keys.length - 1 ? '1px solid var(--lodge-onyx-100)' : 'none' }}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 rounded-lg shrink-0" style={{ background: 'var(--lodge-onyx-50)' }}>
                    <Key size={14} style={{ color: 'var(--lodge-onyx-500)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }} className="truncate">
                      {k.name}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <code style={{ fontSize: 11, color: 'var(--lodge-onyx-500)', fontFamily: 'monospace' }}>
                        {k.prefix}••••••••
                      </code>
                      <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)' }}>
                        Created {new Date(k.createdAt).toLocaleDateString()}
                      </span>
                      {k.lastUsedAt && (
                        <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)' }}>
                          Last used {new Date(k.lastUsedAt).toLocaleDateString()}
                        </span>
                      )}
                      {!k.lastUsedAt && (
                        <span style={{ fontSize: 11, color: 'var(--lodge-onyx-300)' }}>Never used</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(k.id, k.name)}
                  disabled={revoking === k.id}
                  className="p-2 rounded-lg hover:bg-red-50 transition-colors shrink-0 disabled:opacity-50"
                  title="Revoke">
                  {revoking === k.id
                    ? <Loader2 size={14} className="animate-spin" style={{ color: 'var(--color-error)' }} />
                    : <Trash2 size={14} style={{ color: 'var(--color-error)' }} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="px-1 flex items-start gap-2" style={{ fontSize: 11, color: 'var(--lodge-onyx-400)' }}>
        <span className="shrink-0 mt-0.5">&#9432;</span>
        Send the key in the <code style={{ fontFamily: 'monospace' }}>X-Api-Key</code> header on any API request. API keys impersonate your full account access — never commit them to source control or share them.
      </p>
    </div>
  );
};

export default ApiKeysSettings;
